import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Zap, RefreshCw, Save, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { automatikenAPI } from '@/lib/api';

/* /automatiken (22.09.2026, Omoplata-Parität 3.b): alle fest verdrahteten
   Abläufe an einem Ort — an/aus je Ablauf, Wortlaut je Vorlage editierbar
   mit Platzhaltern und Vorschau. Logik bleibt im Code. */

const KANAL = { whatsapp: 'WhatsApp', email: 'E-Mail', telefon: 'Telefon', push: 'Push', aufgabe: 'Aufgabe', intern: 'intern' };
const kanalBadges = (k) => (k || '').split('+').filter(Boolean).map((x) => <Badge key={x} variant="outline" className="text-[10px]">{KANAL[x] || x}</Badge>);
const beispiel = { vorname: 'Lena', studio: 'P1 Performance Center' };
const fuellen = (t) => (t || '').replace(/\{([a-zA-Z_]+)\}/g, (m, p) => beispiel[p] ?? m);

function Ablauf({ a, onSchalter, onTexte, onAbsender, onVersand, versandFrage, versandSetzen, setVersandFrage }) {
  const [offen, setOffen] = useState(false);
  const [werte, setWerte] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setWerte(Object.fromEntries(a.texte.map((t) => [t.key, t.wert]))); }, [a]);
  const geaendert = a.texte.some((t) => (werte[t.key] ?? '') !== (t.wert ?? ''));

  const speichern = async () => {
    setBusy(true);
    try { await onTexte(a.key, werte); } finally { setBusy(false); }
  };
  const zuruecksetzen = async (t) => {
    setBusy(true);
    try { await onTexte(a.key, { [t.key]: '' }); } finally { setBusy(false); }
  };

  return (
    <Card className={`border-border/60 ${a.aktiv ? '' : 'opacity-70'}`} data-testid={`automatik-${a.key}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold">{a.titel}</p>
              {kanalBadges(a.kanal)}
              {a.texte.some((t) => t.geaendert) && <Badge className="text-[10px] bg-amber-500 text-white">Wortlaut angepasst</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">{a.beschreibung}</p>
            <p className="text-xs text-muted-foreground mt-1">Auslöser: {a.trigger} · <span className="font-mono">{a.modul}</span>{a.geaendert_von ? ` · zuletzt ${a.geaendert_von}` : ''}</p>
            {a.hinweis && <p className="text-xs text-amber-700 mt-1">{a.hinweis}</p>}
            {a.versand && (
              <div className="mt-2 text-xs space-y-1" data-testid={`versand-${a.key}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-muted-foreground">Versand der WhatsApp:</span>
                  {[['freigabe', 'Mit Freigabe (Klick)'], ['direkt', 'Automatisch']].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => onVersand(a, k)} data-testid={`versand-${a.key}-${k}`} disabled={!a.aktiv}
                      className={`rounded-full border px-2.5 py-0.5 ${a.versand === k ? (k === 'direkt' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-foreground text-background border-foreground') : 'border-border text-muted-foreground'}`}>{l}</button>
                  ))}
                </div>
                {!a.aktiv && <p className="text-amber-700">{a.aus_hinweis || 'Der Ablauf ist aus: Es entsteht keine WhatsApp-Bestätigung, auch kein Entwurf.'}</p>}
                {a.aktiv && a.versand === 'direkt' && a.lauf_wartet && (
                  <p className="text-amber-700" data-testid={`lauf-wartet-${a.key}`}>Wartet gerade: {a.lauf_wartet}{a.lauf_wartet_seit ? ` (seit ${new Date(a.lauf_wartet_seit).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})` : ''}</p>
                )}
                {a.versand_hinweis && <p className="text-muted-foreground">{a.versand_hinweis}</p>}
                {versandFrage === a.key && (
                  <div className="rounded-md border border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2" data-testid={`versand-frage-${a.key}`}>
                    {/* Erinnerungen ans Probetraining (06.10.2026) bringen ihre eigenen Texte aus dem Register mit */}
                    <p className="font-medium text-sm">{a.versand_frage?.titel || 'Bestätigungen ab jetzt ohne Klick senden?'}</p>
                    <p className="text-muted-foreground">{a.versand_frage?.text || 'Wartende Bestätigungen für kommende Termine gehen raus: tagsüber in den nächsten Minuten, zwischen 21 und 8 Uhr erst ab 8 Uhr. Was die Automatik nicht senden darf, zum Beispiel ohne WhatsApp-Haken, bleibt mit Grund in der Freigabe-Zentrale. Die für vergangene Termine werden geschlossen. Gesendetes lässt sich nicht zurückholen.'}</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => versandSetzen(a.key, 'direkt')} data-testid={`versand-ja-${a.key}`}>Ja, automatisch senden</Button>
                      <Button size="sm" variant="outline" onClick={() => setVersandFrage(null)}>Abbrechen</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {a.absender && (
              <div className="flex items-center gap-2 mt-2 text-xs" data-testid={`absender-${a.key}`}>
                <span className="text-muted-foreground">Absender:</span>
                {[['system', 'Studio-Team'], ['persoenlich', 'Persönlich (zugewiesener Mitarbeiter)']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => onAbsender(a.key, k)}
                    className={`rounded-full border px-2.5 py-0.5 ${a.absender === k ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground'}`}>{l}</button>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {a.schalter ? (
              <label className="flex items-center gap-2 text-xs"><span>{a.aktiv ? 'an' : 'aus'}</span><Switch checked={a.aktiv} onCheckedChange={(v) => onSchalter(a.key, v)} data-testid={`schalter-${a.key}`} /></label>
            ) : <Badge variant="outline" className="text-[10px]">{a.aktiv ? 'an' : 'aus'}</Badge>}
            {a.texte.length > 0 && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOffen((o) => !o)}>
                {offen ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}{a.texte.length} Vorlagen
              </Button>
            )}
          </div>
        </div>

        {offen && (
          <div className="space-y-3 border-t border-border/50 pt-3">
            {a.texte.map((t) => (
              <div key={t.key} className="grid md:grid-cols-2 gap-3" data-testid={`vorlage-${a.key}-${t.key}`}>
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-xs font-medium">{t.label}</label>
                    <span className="text-[10px] text-muted-foreground">{(t.platzhalter || []).map((p) => `{${p}}`).join(' ') || 'keine Platzhalter'}</span>
                  </div>
                  {t.mehrzeilig || (werte[t.key] || '').length > 120
                    ? <Textarea rows={5} value={werte[t.key] ?? ''} onChange={(e) => setWerte((w) => ({ ...w, [t.key]: e.target.value }))} className="text-sm" />
                    : <Input value={werte[t.key] ?? ''} onChange={(e) => setWerte((w) => ({ ...w, [t.key]: e.target.value }))} className="text-sm" />}
                  {t.geaendert && <button type="button" className="text-[11px] text-muted-foreground hover:underline inline-flex items-center gap-1" onClick={() => zuruecksetzen(t)}><RotateCcw className="w-3 h-3" /> auf Vorgabe zurücksetzen</button>}
                </div>
                <div className="rounded-md bg-muted/40 p-3 text-sm whitespace-pre-wrap">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Vorschau</p>
                  {fuellen(werte[t.key])}
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <Button size="sm" onClick={speichern} disabled={!geaendert || busy} className="gap-1.5" data-testid={`speichern-${a.key}`}><Save className="w-3.5 h-3.5" /> Wortlaut speichern</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AutomatikenPage() {
  const [ablaeufe, setAblaeufe] = useState([]);
  const [laden, setLaden] = useState(true);

  const lade = useCallback(async () => {
    setLaden(true);
    try { setAblaeufe((await automatikenAPI.liste()).data.ablaeufe || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Automatiken konnten nicht geladen werden'); }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { lade(); }, [lade]);

  const ersetzen = (neu) => setAblaeufe((l) => l.map((a) => (a.key === neu.key ? neu : a)));
  const onAbsender = async (key, absender) => {
    try { ersetzen((await automatikenAPI.setzen(key, { absender })).data); toast.success(absender === 'persoenlich' ? 'Persönlicher Absender' : 'Absender: Studio-Team'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };
  // Versand ohne Freigabe einschalten: erst Rückfrage (es gehen sofort wartende Nachrichten raus), ausschalten direkt
  const [versandFrage, setVersandFrage] = useState(null);
  const versandSetzen = async (key, versand) => {
    try {
      const neu = (await automatikenAPI.setzen(key, { versand })).data;
      ersetzen(neu); setVersandFrage(null);
      const was = neu.nachrichten || 'Bestätigungen';
      toast.success(versand === 'direkt' ? `${was} gehen jetzt automatisch raus` : `${was} warten wieder auf Freigabe`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
  };
  const onVersand = (a, versand) => {
    if (versand === a.versand) return;
    if (versand === 'direkt') setVersandFrage(a.key); else versandSetzen(a.key, versand);
  };
  const onSchalter = async (key, aktiv) => {
    try { ersetzen((await automatikenAPI.setzen(key, { aktiv })).data); toast.success(aktiv ? 'Eingeschaltet' : 'Ausgeschaltet'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
  };
  const onTexte = async (key, texte) => {
    try { ersetzen((await automatikenAPI.setzen(key, { texte })).data); toast.success('Wortlaut gespeichert'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Nicht gespeichert'); }
  };

  const gruppen = [...new Set(ablaeufe.map((a) => a.gruppe))];

  return (
    <div className="space-y-5 fade-in" data-testid="automatiken-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Zap className="w-6 h-6 text-primary" /> Automatiken</h1>
          <p className="text-muted-foreground text-sm">Alle Abläufe, die von selbst laufen — an/aus und Wortlaut. Die Logik dahinter ist fest im Code.</p>
        </div>
        <Button variant="outline" size="sm" onClick={lade} disabled={laden} className="gap-1.5"><RefreshCw className={`w-3.5 h-3.5 ${laden ? 'animate-spin' : ''}`} /> Aktualisieren</Button>
      </div>
      {gruppen.map((g) => (
        <section key={g} className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{g}</h2>
          {ablaeufe.filter((a) => a.gruppe === g).map((a) => <Ablauf key={a.key} a={a} onSchalter={onSchalter} onTexte={onTexte} onAbsender={onAbsender}
            onVersand={onVersand} versandFrage={versandFrage} versandSetzen={versandSetzen} setVersandFrage={setVersandFrage} />)}
        </section>
      ))}
    </div>
  );
}
